"use client";

// Contract file for the reservations convenience batch. The picker-speed
// session implements these against a Convex day-availability query; the
// form-conveniences session consumes them. Signatures are frozen so both
// sessions can build independently.

import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

export type DayAvailability = "open" | "limited" | "full";

/** Add days to a YYYY-MM-DD string (noon-anchored to dodge DST edges). */
function addDays(date: string, days: number): string {
  const d = new Date(date + "T12:00:00");
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Warm the Convex client cache for the given dates so the time grid renders
 * instantly when the guest lands on the time step. Hardcodes two slots to keep
 * the hook count stable across renders; extra dates are ignored.
 */
export function usePrefetchDayAvailability(
  dates: string[],
  partySize: number,
  locationId?: string,
): void {
  const first = dates[0];
  const second = dates[1];

  useQuery(
    api.reservationAvailability.getSlots,
    first ? { date: first, partySize, locationId } : "skip",
  );
  useQuery(
    api.reservationAvailability.getSlots,
    second ? { date: second, partySize, locationId } : "skip",
  );
}

/**
 * Per-day availability for a 35-day window starting at monthStartDate, keyed by
 * YYYY-MM-DD. Returns undefined while loading or until implemented; callers must
 * treat undefined as "no availability styling".
 */
export function useDayAvailabilityMap(
  monthStartDate: string,
  partySize: number,
  locationId?: string,
): Record<string, DayAvailability> | undefined {
  const endDate = monthStartDate ? addDays(monthStartDate, 34) : monthStartDate;

  const result = useQuery(
    api.reservationAvailability.getDayAvailability,
    monthStartDate
      ? { startDate: monthStartDate, endDate, partySize, locationId }
      : "skip",
  );

  return useMemo(() => {
    if (result === undefined) return undefined;
    return result.reduce<Record<string, DayAvailability>>((acc, day) => {
      acc[day.date] = day.status;
      return acc;
    }, {});
  }, [result]);
}
